import React from 'react';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import NumberInput from '../shared/NumberInput';
import PickSelect from '../shared/PickSelect';
import { labelFor, OPP_LOAN_TYPE_LABELS, OPP_LOAN_PURPOSE_LABELS, OCCUPANCY_LABELS } from '../shared/labels';
import { displayName } from '../kit/OwnerChip';

// Ticket 209-3: the parameter form for each workflow action, in place of a hand-typed JSON box.
// The keys and limits mirror backend/automation_actions.py; an action the engine does not run yet
// (connectors not wired) keeps a JSON box, which refuses invalid JSON instead of dropping it.

const opts = (values, map) => values.map((v) => ({ value: v, label: labelFor(map, v) }));

const PRIORITIES = opts(['low', 'medium', 'high']);
const SLOTS = [
  { value: 'loan_officer', label: 'Loan Officer' },
  { value: 'processor', label: 'Processor' },
  { value: 'loan_officer_assistant', label: 'Loan Officer Assistant' },
  { value: 'business_development', label: 'Business Development' },
  { value: 'underwriter', label: 'Underwriter' },
];

const LEAD_STATUSES = ['new', 'attempted', 'contacted', 'qualified', 'nurture', 'active', 'converted', 'unresponsive', 'disqualified', 'closed'];
const CONTACT_TYPES = ['borrower', 'co_borrower', 'buyer', 'seller', 'past_client', 'sphere', 'realtor', 'referral_partner', 'builder', 'title', 'attorney', 'cpa', 'lender', 'investor', 'renter', 'lead', 'other'];
const COMM_PREFS = ['email', 'sms', 'phone', 'any', 'do_not_contact'];
const PROPERTY_TYPES = ['sfr', 'condo', 'townhouse', 'multi_family', 'manufactured', 'land', 'commercial', 'other'];

// What "Update a field" may set. Same lists as CONTACT_FIELDS / OPPORTUNITY_FIELDS in
// automation_actions.py; the engine rejects anything else.
export const UPDATE_FIELDS = {
  contact: [
    { key: 'lead_status', label: 'Lead status', options: opts(LEAD_STATUSES) },
    { key: 'contact_type', label: 'Contact type', options: opts(CONTACT_TYPES) },
    { key: 'preferred_language', label: 'Preferred language' },
    { key: 'communication_preference', label: 'Communication preference', options: opts(COMM_PREFS) },
    { key: 'referral_source', label: 'Referral source' },
    { key: 'estimated_lifetime_value', label: 'Estimated lifetime value', number: true },
  ],
  opportunity: [
    { key: 'loan_purpose', label: 'Loan purpose', options: opts(Object.keys(OPP_LOAN_PURPOSE_LABELS), OPP_LOAN_PURPOSE_LABELS) },
    { key: 'loan_type', label: 'Loan type', options: opts(Object.keys(OPP_LOAN_TYPE_LABELS), OPP_LOAN_TYPE_LABELS) },
    { key: 'program', label: 'Program' },
    { key: 'property_type', label: 'Property type', options: opts(PROPERTY_TYPES) },
    { key: 'occupancy', label: 'Occupancy', options: opts(Object.keys(OCCUPANCY_LABELS), OCCUPANCY_LABELS) },
    { key: 'loan_amount', label: 'Loan amount', number: true },
    { key: 'interest_rate', label: 'Interest rate (%)', number: true },
    { key: 'estimated_closing_date', label: 'Estimated closing date', date: true },
    { key: 'preapproval_expires_at', label: 'Preapproval expires', date: true },
    { key: 'notes', label: 'Notes' },
  ],
};

// kind: text | textarea | select | integer | staff | stage
const FORMS = {
  create_task: [
    { key: 'title', label: 'Task title', kind: 'text', required: true, max: 200 },
    { key: 'description', label: 'Description', kind: 'textarea', max: 5000 },
    { key: 'priority', label: 'Priority', kind: 'select', options: PRIORITIES, fallback: 'medium' },
    { key: 'due_in_days', label: 'Due in (days)', kind: 'integer' },
    { key: 'owner_id', label: 'Assign to', kind: 'staff', blank: "The contact's owner" },
  ],
  add_internal_note: [{ key: 'content', label: 'Note', kind: 'textarea', required: true, max: 5000 }],
  move_stage: [{ key: 'stage', label: 'Move the deal to', kind: 'stage', required: true }],
  assign_user: [
    { key: 'user_id', label: 'Assign to', kind: 'staff', required: true },
    { key: 'slot', label: 'As', kind: 'select', options: SLOTS, fallback: 'loan_officer' },
  ],
  add_tag: [{ key: 'tag', label: 'Tag', kind: 'text', required: true, max: 100 }],
  remove_tag: [{ key: 'tag', label: 'Tag', kind: 'text', required: true, max: 100 }],
  // No template library exists yet; the name is the key recipes already use.
  send_email: [{ key: 'template', label: 'Template name', kind: 'text' }],
  send_sms: [{ key: 'template', label: 'Template name', kind: 'text' }],
};
const HAS_FORM = (type) => Boolean(FORMS[type]) || type === 'update_field';

const isBlank = (v) => v === undefined || v === null || (typeof v === 'string' && !v.trim());

function setKey(params, key, value) {
  const next = { ...params };
  if (isBlank(value)) delete next[key]; else next[key] = value;
  return next;
}

function parseJsonObject(text) {
  try {
    const v = JSON.parse(text);
    return v && typeof v === 'object' && !Array.isArray(v) ? { value: v } : { error: 'Params must be a JSON object, like {"key": "value"}' };
  } catch {
    return { error: 'Not valid JSON' };
  }
}

// The first thing wrong with the steps, worded for the person saving, or null.
export function actionsProblem(actions) {
  for (let i = 0; i < actions.length; i++) {
    const a = actions[i];
    const where = `Step ${i + 1} (${labelFor(null, a.type)})`;
    if (a.paramsText !== undefined) {
      const r = parseJsonObject(a.paramsText);
      if (r.error) return `${where}: ${r.error}`;
    }
    const p = a.params || {};
    for (const f of FORMS[a.type] || []) {
      if (f.required && isBlank(p[f.key])) return `${where}: ${f.label} is required`;
    }
    if (a.type === 'update_field') {
      if (isBlank(p.field)) return `${where}: pick the field to change`;
      if (isBlank(p.value)) return `${where}: enter the new value`;
    }
  }
  return null;
}

// What goes to the API: the form's scratch text is not part of an action.
export const withoutScratch = (a) => { const out = { ...a }; delete out.paramsText; return out; };
export const toApiActions = (actions) => actions.map(withoutScratch);

function Field({ label, children }) {
  return (
    <label className="block min-w-0">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function JsonParams({ action, onChange, testId }) {
  const text = action.paramsText ?? JSON.stringify(action.params || {});
  const problem = action.paramsText !== undefined ? parseJsonObject(text).error : null;
  return (
    <div>
      <Field label="Params (JSON)">
        <Input
          value={text} data-testid={testId} aria-invalid={Boolean(problem)}
          placeholder='{"key": "value"}'
          onChange={(e) => {
            const r = parseJsonObject(e.target.value);
            onChange(r.value ? { params: r.value, paramsText: e.target.value } : { paramsText: e.target.value });
          }}
        />
      </Field>
      {problem && <p className="text-xs text-red-500 mt-1" data-testid={`${testId}-error`}>{problem}</p>}
      <p className="text-xs text-slate-400 mt-1">This step is not delivered yet, so it only records a dry run.</p>
    </div>
  );
}

function FormField({ f, value, onSet, staff, stages, testId }) {
  if (f.kind === 'textarea') {
    return <Textarea rows={2} maxLength={f.max} value={value ?? ''} data-testid={testId} onChange={(e) => onSet(e.target.value)} />;
  }
  if (f.kind === 'integer') {
    return <NumberInput integer placeholder="Same day" value={value ?? null} data-testid={testId} onChange={(n) => onSet(n)} />;
  }
  if (f.kind === 'select') {
    return <PickSelect value={value || f.fallback} options={f.options} testId={testId} onChange={(v) => onSet(v)} />;
  }
  if (f.kind === 'staff') {
    const options = [
      ...(f.blank ? [{ value: '', label: f.blank }] : []),
      ...staff.map((m) => ({ value: m.user_id, label: displayName(m) })),
    ];
    return <PickSelect value={value || ''} options={options} placeholder="Pick a person" testId={testId} onChange={(v) => onSet(v)} />;
  }
  if (f.kind === 'stage') {
    const options = stages.map((s) => ({ value: s.key, label: s.label || s.key }));
    // A stage the tenant has since renamed or hidden stays visible so the step can be fixed.
    if (value && !options.some((o) => o.value === value)) options.unshift({ value, label: labelFor(null, value) });
    return <PickSelect value={value || ''} options={options} placeholder="Pick a stage" testId={testId} onChange={(v) => onSet(v)} />;
  }
  return <Input maxLength={f.max} value={value ?? ''} data-testid={testId} onChange={(e) => onSet(e.target.value)} />;
}

function UpdateFieldParams({ params, onSetParams, testId }) {
  const target = params.target || 'contact';
  const fields = UPDATE_FIELDS[target];
  const spec = fields.find((f) => f.key === params.field);
  const setValue = (v) => onSetParams(setKey(params, 'value', v));
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
      <Field label="Change a">
        <PickSelect value={target} testId={`${testId}-target`}
          options={[{ value: 'contact', label: 'Contact' }, { value: 'opportunity', label: 'Mortgage deal' }]}
          onChange={(v) => onSetParams({ ...params, target: v, field: undefined, value: undefined })} />
      </Field>
      <Field label="Field">
        <PickSelect value={params.field || ''} placeholder="Pick a field" testId={`${testId}-field`}
          options={fields.map((f) => ({ value: f.key, label: f.label }))}
          onChange={(v) => onSetParams(setKey({ ...params, value: undefined }, 'field', v))} />
      </Field>
      <Field label="New value">
        {spec?.options ? (
          <PickSelect value={params.value || ''} placeholder="Pick a value" options={spec.options} testId={`${testId}-value`} onChange={setValue} />
        ) : spec?.number ? (
          <NumberInput value={typeof params.value === 'number' ? params.value : null} data-testid={`${testId}-value`} onChange={(n) => setValue(n)} />
        ) : spec?.date ? (
          <Input type="date" value={params.value || ''} data-testid={`${testId}-value`} onChange={(e) => setValue(e.target.value)} />
        ) : (
          <Input value={params.value ?? ''} disabled={!spec} data-testid={`${testId}-value`} onChange={(e) => setValue(e.target.value)} />
        )}
      </Field>
    </div>
  );
}

// `action` is the composer's row: { type, delay_minutes, params, paramsText? }.
// onChange gets the pieces of the row that changed.
export default function ActionParams({ action, index, staff = [], stages = [], onChange }) {
  const params = action.params || {};
  const testId = `action-${index}`;
  if (!HAS_FORM(action.type)) return <JsonParams action={action} onChange={onChange} testId={`${testId}-json`} />;
  if (action.type === 'update_field') {
    return <UpdateFieldParams params={params} testId={testId} onSetParams={(p) => onChange({ params: p })} />;
  }
  const fields = FORMS[action.type];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {fields.map((f) => (
        <div key={f.key} className={f.kind === 'textarea' ? 'sm:col-span-2' : ''}>
          <Field label={f.required ? `${f.label} *` : f.label}>
            <FormField f={f} value={params[f.key]} staff={staff} stages={stages} testId={`${testId}-${f.key}`}
              onSet={(v) => onChange({ params: setKey(params, f.key, v) })} />
          </Field>
        </div>
      ))}
      {(action.type === 'send_email' || action.type === 'send_sms') && (
        <p className="sm:col-span-2 text-xs text-slate-400">Not delivered yet — a run records a dry run and nothing is sent.</p>
      )}
    </div>
  );
}
